import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
	createTheme,
	StyledEngineProvider,
	ThemeProvider,
} from "@mui/material/styles";
import clsx from "clsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as Api from "./Api";
import * as Flags from "./lib/Flags";
import * as NavigationUtils from "./lib/util/NavigationUtils";
import * as UserSelectors from "./store/User/User.selector";
import * as LocaleUtils from "./lib/util/LocaleUtils";
import * as NotificationUtils from "lib/util/NotificationUtils";

import routerManager from "./Routing";
import PrivacyModal from "./components/custom/PrivacyModal/PrivacyModal";
import Background from "./components/common/Background/Background";
import SpinnerOverlay from "./components/custom/SpinnerOverlay/SpinnerOverlay";
import ScrollToTop from "./components/custom/ScrollToTop/ScrollToTop";
import FatalError from "./components/custom/FatalError/FatalError";
import DebugArea from "./components/common/DebugArea/DebugArea";
import Header from "./components/custom/Header/Header";
import SplashNotification from "./components/custom/SplashNotification/SplashNotification";
import NotificationBanner from "./components/custom/NotificationBanner/NotificationBanner";
import ErrorBoundary from "./components/util/ErrorBoundary/ErrorBoundary";
import { AboveNavContext } from "./components/nav/Navigation/Navigation";
import { useDispatch, useSelector } from "./lib/hook/ReduxHooks";
import {
	useHeaderVisibility,
	useNotificationVisible,
	useScrollTopVisibility,
} from "./lib/hook/NavigationHooks";
import { useFlag } from "./lib/hook/MiscHooks";
import { ViewContext } from "lib/util/ReactUtils";
import { updateUserObject } from "./store/User/User.action";

const theme = createTheme({
	palette: {
		primary: {
			main: "#f9c509",
		},
		secondary: {
			main: "#f9c509",
		},
	},
});

const RouterContent = ({ routes, renderId }: any) => {
	const headerVisible = useHeaderVisibility();
	const scrollToTopVisible = useScrollTopVisibility();
	const localeContext = useContext(LocaleUtils.LocaleContext);
	const dispatch = useDispatch();
	const [portalNotification, setPortalNotification] =
		useState<NotificationUtils.PortalNotification>({});
	const bannerNotificationVisible = useNotificationVisible(
		NotificationUtils.NOTIFICATION_TYPE_BANNER,
		portalNotification.level,
	);

	useEffect(() => {
		const getNotifcationLevel = async () => {
			const notification = await Api.fetchPortalNotificationLevel.submit({
				options: { cache: false },
			});
			setPortalNotification(notification.data);
		};

		const interval = window.setInterval(
			getNotifcationLevel,
			NotificationUtils.POLLING_INTERVAL,
		);
		getNotifcationLevel();

		return () => window.clearInterval(interval);
	}, [setPortalNotification, localeContext]);

	const { additionalFleets, loggedInUser } = useSelector((state) => {
		const loggedInUser = UserSelectors.selectLoggedInUser(state);

		return {
			loggedInUser: loggedInUser,
			additionalFleets: loggedInUser?.additionalFleets,
		};
	});
	useEffect(() => {
		if (loggedInUser?.uid) {
			if (loggedInUser.portalLocale !== localeContext.code) {
				Api.postUserUpdate.submit({
					params: { userId: loggedInUser.uid },
					body: {
						portalLocale: localeContext.code,
					},
				});
				dispatch(updateUserObject({ portalLocale: localeContext.code }));
			}
		}
	}, [localeContext.code, loggedInUser, dispatch, localeContext.locale]);

	const anonymousMode = useFlag(Flags.ANONYMOUS_MODE);
	const fixedContentRef = useRef<any>();
	const fixedContentViewValue = useMemo(
		() => () => fixedContentRef.current,
		[],
	);

	return (
		<>
			<main
				className={clsx(
					"Router__content",
					headerVisible && "--has-nav",
					headerVisible && additionalFleets && "--has-multi-fleet",
					headerVisible && anonymousMode.is() && "--has-anonymous-mode",
					headerVisible && bannerNotificationVisible && "--has-notification",
				)}
			>
				<Background />
				{React.cloneElement(routes, { key: renderId })}
				{scrollToTopVisible && <ScrollToTop />}
			</main>
			<div ref={fixedContentRef} className={"FixedContent mui-fixed"}>
				<ViewContext.Provider value={fixedContentViewValue}>
					<AboveNavContext.Provider value={true}>
						<NotificationBanner notification={portalNotification} />
						<Header key={renderId} />
						<SpinnerOverlay />
						<DebugArea />
					</AboveNavContext.Provider>
				</ViewContext.Provider>
			</div>
			<PrivacyModal />
			<SplashNotification notification={portalNotification} />
			<ToastContainer />
		</>
	);
};

const App = () => {
	const [resetKey, setResetKey] = useState(0);
	const [renderId, setRenderId] = useState(0);

	const dispatch = useDispatch();

	useEffect(() => {
		NavigationUtils.provideRefreshHandler((force) => {
			if (force) {
				return (window.location as any).reload(force);
			}
			setRenderId((i) => i + 1);
		});
	}, [dispatch]);

	useEffect(() => {
		const id = NavigationUtils.onHistoryChange(() => {
			setResetKey((resetKey) => resetKey + 1);
		});

		return () => NavigationUtils.clearHistoryChange(id);
	});

	return (
		<StyledEngineProvider injectFirst={true}>
			<ThemeProvider theme={theme}>
				<LocaleUtils.LocaleProvider>
					<ErrorBoundary fatal={true} resetKey={resetKey}>
						{(topLevelError: Error | null) =>
							topLevelError ? (
								<FatalError error={topLevelError} />
							) : (
								React.createElement(routerManager.component, {
									children: (routes: any) => (
										<RouterContent renderId={renderId} routes={routes} />
									),
								} as any)
							)
						}
					</ErrorBoundary>
				</LocaleUtils.LocaleProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

export default React.memo(App);
